import type { ReactNode } from 'react'
import phoneRender from 'url:asset/launchpad/iphone-16-pink.png'

type TiltedPhoneProps = {
  children?: ReactNode
  className?: string
  width?: number
}

// iphone-16-pink.png is 558×874. These are the display's corners in that image
// (TL, TR, BR, BL), found by fitting lines to the screen edges. The right edge is
// pushed 3px into the bezel, which renders thicker on that side than the left.
const RENDER = { w: 558, h: 874 }
const SCREEN_CORNERS = [[213.9, 45.4], [522.2, 31.4], [289.3, 830.8], [5.4, 792.0]]

// Content is laid out flat at iPhone 16 point size, then warped onto the display
const SCREEN = { w: 393, h: 852, radius: 55 }
const ISLAND = { w: 126, h: 37, top: 11 }

export default function TiltedPhone({ children, className = '', width = 256 }: TiltedPhoneProps) {
  const scale = width / RENDER.w
  const corners = SCREEN_CORNERS.map(([x, y]) => [x * scale, y * scale])

  return (
    <div className={className} style={{ position: 'relative', width, height: (width * RENDER.h) / RENDER.w }}>
      <img src={phoneRender} draggable={false} style={{ display: 'block', width: '100%', height: '100%' }} />

      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: SCREEN.w,
          height: SCREEN.h,
          borderRadius: SCREEN.radius,
          overflow: 'hidden',
          background: '#1a1a1a',
          transformOrigin: '0 0',
          transform: quadTransform(SCREEN.w, SCREEN.h, corners),
        }}
      >
        {children}

        <div
          style={{
            position: 'absolute',
            top: ISLAND.top,
            left: (SCREEN.w - ISLAND.w) / 2,
            width: ISLAND.w,
            height: ISLAND.h,
            borderRadius: ISLAND.h / 2,
            background: '#000',
          }}
        />
      </div>
    </div>
  )
}

// CSS matrix3d that maps a w×h box onto four points [TL, TR, BR, BL]
function quadTransform(w: number, h: number, dst: number[][]) {
  const src = [[0, 0], [w, 0], [w, h], [0, h]]
  const A: number[][] = []
  const b: number[] = []
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i]
    const [u, v] = dst[i]
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u)
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v)
  }

  // Gauss-Jordan elimination with partial pivoting
  for (let c = 0; c < 8; c++) {
    let p = c
    for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r
    ;[A[c], A[p]] = [A[p], A[c]]
    ;[b[c], b[p]] = [b[p], b[c]]
    for (let r = 0; r < 8; r++) {
      if (r === c) continue
      const f = A[r][c] / A[c][c]
      for (let k = c; k < 8; k++) A[r][k] -= f * A[c][k]
      b[r] -= f * b[c]
    }
  }

  const [a, b1, c, d, e, f, g, h2] = b.map((v, i) => v / A[i][i])
  return `matrix3d(${a},${d},0,${g},${b1},${e},0,${h2},0,0,1,0,${c},${f},0,1)`
}
